import React, { useState } from 'react';

import { X } from 'lucide-react';

import { useGetCustomRatesListQuery, useUploadStatementMutation } from '../api';
import {
	useCreateMerchantRecordMutation,
	useDeleteEmptyMerchantRecordMutation,
} from '../api/merchantRecords';
import { useFeature } from '../hooks';
import { useAppDispatch, useTypedSelector } from '../store';
import { clearDirectRates } from '../store/configSlice';
import { MAX_MERCHANT_MONTHS } from '../utils/merchantRecord';
import {
	isStatementImage,
	isStatementUploadFile,
	STATEMENT_UPLOAD_ACCEPT,
	STATEMENT_UPLOAD_ERROR,
	STATEMENT_UPLOAD_HINT,
	sortStatementPhotos,
	statementFilesError,
} from '../utils/statementUploadFormats';

import { Button, FileUploadDropzone, SelectInput } from './common';
import CustomizeFeeStructureModal from './CustomizeFeeStructureModal';
import StatementUploadV9 from './StatementUploadV9';
import ViewRatesModal from './settings/ViewRatesModal';

import type { RiskTier } from '../api';
import type { UploadConfig } from '../types';

// One flat choice (ROC-116): the API tells a Stripe export from a flat-rate
// statement by the file's content, so the user no longer picks between them.
type UiRateType = 'IC_PLUS' | 'TIERED' | 'FLAT_TIERED';

// Helper function to get human-readable labels for rate types
const getRateTypeLabel = (type: UiRateType | ''): string => {
	switch (type) {
		case 'IC_PLUS':
			return 'Interchange Plus';
		case 'TIERED':
			return 'Tiered';
		case 'FLAT_TIERED':
			return 'Flat Rate';
		default:
			return type;
	}
};

interface StatementUploadProps {
	onClose: () => void;
	onSuccess: (uploadedFileId?: string, merchantRecordId?: string) => void;
	uploadConfig?: UploadConfig;
	mfe?: boolean;
}

const StatementUpload: React.FC<StatementUploadProps> = ({
	onClose,
	onSuccess,
	uploadConfig,
	mfe = false,
}) => {
	const dispatch = useAppDispatch();
	const directRates = useTypedSelector((state) => state.config.directRates);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [dragActive, setDragActive] = useState(false);
	const [error, setError] = useState('');
	const [selectedConfigId, setSelectedConfigId] = useState('');
	const [selectedRateTypes, setSelectedRateTypes] = useState<UiRateType[]>([
		'IC_PLUS',
	]);
	const [selectedRiskTier, setSelectedRiskTier] = useState<RiskTier>('LOW');
	const [showViewRatesModal, setShowViewRatesModal] = useState(false);
	const [showCustomizeModal, setShowCustomizeModal] = useState(false);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	// Flat Tiered specific fields
	const [businessType, setBusinessType] = useState('');
	// Target output format for cross-type conversion (e.g., FLAT_TIERED -> IC_PLUS)
	const [targetRateType, setTargetRateType] = useState<UiRateType | ''>('');

	const [uploadStatement, { isLoading: uploadingOne }] =
		useUploadStatementMutation();
	const [createMerchantRecord] = useCreateMerchantRecordMutation();
	const [deleteEmptyMerchantRecord] = useDeleteEmptyMerchantRecordMutation();
	const [merchantName, setMerchantName] = useState('');
	const [submittingMonths, setSubmittingMonths] = useState(false);
	const uploading = uploadingOne || submittingMonths;
	// Several files that are not all photos are months of one merchant (ROC-104);
	// several photos stay the pages of one statement.
	// Merchant records are per tenant (flag merchant_records); without it several
	// non-photo files stay an error, as before.
	const monthsAllowed = useFeature('merchant_records');
	const monthsMode =
		monthsAllowed &&
		selectedFiles.length > 1 &&
		!selectedFiles.every(isStatementImage);

	const isAnyFlat = selectedRateTypes.includes('FLAT_TIERED');

	// Fetch config list
	const { data: configsResponse, isLoading: isLoadingConfigs } =
		useGetCustomRatesListQuery({
			page: 1,
			page_size: 50, // Fetch all configs
		});
	const configs = configsResponse?.data || [];

	// Set default selection to first config if available
	React.useEffect(() => {
		if (configs.length > 0 && !selectedConfigId) {
			const defaultConfig = configs.filter((config) => config.is_default)[0];
			setSelectedConfigId(defaultConfig?.id || '');
		}
	}, [configs, selectedConfigId]);

	// Get available output format options based on input statement type
	// Currently only FLAT_TIERED -> IC_PLUS conversion is supported
	const inputStatementType = selectedRateTypes[0];
	const getOutputFormatOptions = () => {
		if (inputStatementType === 'FLAT_TIERED') {
			return [
				{
					value: inputStatementType,
					label: 'Flat Rate (Same as input)',
					isDefault: true,
				},
				{
					value: 'IC_PLUS',
					label: 'Interchange Plus (Convert)',
					isDefault: false,
				},
			];
		}
		// Other types don't support conversion yet - show only the input type
		return [
			{
				value: inputStatementType,
				label: getRateTypeLabel(inputStatementType),
				isDefault: true,
			},
		];
	};

	const outputFormatOptions = getOutputFormatOptions();
	const hasMultipleOutputOptions = outputFormatOptions.length > 1;

	// Auto-select target rate type to match input statement type when it changes
	React.useEffect(() => {
		// Default target to same as input statement type
		setTargetRateType(inputStatementType as UiRateType);
	}, [inputStatementType]);

	React.useEffect(() => {
		if (isAnyFlat) setBusinessType((prev) => prev || 'retail');
	}, [isAnyFlat]);

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			handleFiles(Array.from(e.dataTransfer.files));
		}
	};

	const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			handleFiles(Array.from(e.target.files));
		}
	};

	const handleFiles = (picked: File[]) => {
		const files = picked.length > 1 ? sortStatementPhotos(picked) : picked;
		const file = files[0];
		if (!file) return;
		if (monthsAllowed && files.length > 1 && !files.every(isStatementImage)) {
			if (files.length > MAX_MERCHANT_MONTHS) {
				setError(`Pick up to ${MAX_MERCHANT_MONTHS} statements, one per month.`);
				return;
			}
			if (!files.every(isStatementUploadFile)) {
				setError(STATEMENT_UPLOAD_ERROR);
				return;
			}
		} else {
			const filesError = statementFilesError(files);
			if (filesError) {
				setError(filesError);
				return;
			}
		}
		if (files.some((f) => f.size > 10 * 1024 * 1024)) {
			// 10MB limit per file
			setError('Each file must be less than 10MB.');
			return;
		}
		setError('');
		setSelectedFile(file);
		setSelectedFiles(files);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedFile) {
			return;
		}

		if (isAnyFlat && !businessType) {
			setError('Please select a business type for flat statements.');
			return;
		}

		if (monthsMode && !merchantName.trim()) {
			setError('Enter the merchant name.');
			return;
		}

		setError('');

		try {
			const formData = new FormData();
			formData.append('statement_type', 'vendor_statement');

			// Append rate plan type (use first selected type for backward compatibility)
			formData.append('rate_plan_type', selectedRateTypes[0]);

			// Append selected risk tier for revenue calculation
			formData.append('selected_risk_tier', selectedRiskTier);

			// Extraction engine is no longer selectable — the backend defaults
			// everything to the platform engine.

			// Append target_rate_type for cross-type conversion (applicable to all statement types)
			if (targetRateType && targetRateType !== selectedRateTypes[0]) {
				// Only send target_rate_type if it's different from input type (conversion requested)
				formData.append('target_rate_type', targetRateType);
			}

			if (isAnyFlat) {
				formData.append('business_type', businessType);
			}

			// If direct_rates is available, use it instead of custom_rates_id
			if (directRates) {
				formData.append('direct_rates', JSON.stringify(directRates));
				// Clear direct_rates after use
				dispatch(clearDirectRates());
			} else if (selectedConfigId) {
				formData.append('custom_rates_id', selectedConfigId);
			}

			if (monthsMode) {
				setSubmittingMonths(true);
				try {
					const record = await createMerchantRecord({
						merchant_name: merchantName.trim(),
					}).unwrap();
					// One upload per month, sent together; the API runs them in parallel.
					const results = await Promise.allSettled(
						selectedFiles.map((f) => {
							const monthForm = new FormData();
							formData.forEach((value, key) => monthForm.append(key, value));
							monthForm.append('file', f);
							monthForm.append('merchant_record_id', record.id);
							return uploadStatement({ formData: monthForm, uploadConfig }).unwrap();
						}),
					);
					const failed = selectedFiles.filter(
						(_, i) => results[i].status === 'rejected',
					);
					if (failed.length === selectedFiles.length) {
						// Nothing reached the record: remove it so no empty record is left.
						await deleteEmptyMerchantRecord(record.id).unwrap().catch(() => undefined);
					}
					if (failed.length) {
						const reason = results.find(
							(r): r is PromiseRejectedResult => r.status === 'rejected',
						)?.reason;
						setError(
							`${failed.map((f) => f.name).join(', ')} did not upload: ${
								reason?.data?.message || 'please try again'
							}.${failed.length < selectedFiles.length ? ' The other months were uploaded; open the merchant from the statements list.' : ''}`,
						);
						return;
					}
					onSuccess(undefined, record.id);
					onClose();
				} finally {
					setSubmittingMonths(false);
				}
				return;
			}

			(selectedFiles.length ? selectedFiles : [selectedFile]).forEach((f) =>
				formData.append('file', f),
			);
			const result = await uploadStatement({
				formData,
				uploadConfig,
			}).unwrap();

			// Store the fileId for potential customization
			setSelectedFileId(result.file_id);
			setSelectedFile(null);
			setSelectedFiles([]);

			onSuccess(result.file_id);
			onClose();
		} catch (err: any) {
			setError(err?.data?.message || 'Upload failed. Please try again.');
		}
	};

	const removeFile = () => {
		setSelectedFile(null);
		setSelectedFiles([]);
		setError('');
	};

	const v9Upload = useFeature('statement_states');

	const handleCancel = () => {
		// Clear any customized direct rates when canceling
		if (directRates) {
			dispatch(clearDirectRates());
		}
		onClose();
	};

	if (v9Upload) {
		return (
			<StatementUploadV9
				files={selectedFiles}
				error={error}
				configs={configs}
				configId={selectedConfigId}
				onConfigChange={setSelectedConfigId}
				onFiles={handleFiles}
				onCancel={handleCancel}
				onSubmit={handleSubmit}
				uploading={uploading}
				monthsMode={monthsMode}
				monthsAllowed={monthsAllowed}
				merchantName={merchantName}
				onMerchantNameChange={setMerchantName}
			/>
		);
	}

	return (
		<div
			className={`fixed inset-0 ${mfe ? '' : 'bg-black/40'} flex items-center justify-center z-50 p-4`}
		>
			<div className="relative w-[800px] max-w-full bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-200 shadow-xl flex flex-col overflow-hidden">
				{/* Header */}
				<div className="flex items-center gap-2.5 p-5">
					<div className="flex-1 text-black text-lg font-medium leading-tight">
						Upload Statement
					</div>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleCancel}
						className="w-6 h-6 p-0 text-neutral-500 hover:text-neutral-700"
						icon={X}
					/>
				</div>

				{/* Content */}
				<div className="flex-1 px-5 pb-5">
					<div className="flex h-full flex-col gap-5">
						{/* Fee Structure Config Selection */}
						<div className="flex flex-row gap-2 align-center">
							{/* Rate Plan Type Selection */}
							<SelectInput
								label="Statement Type"
								value={selectedRateTypes[0] || 'IC_PLUS'}
								options={[
									{ label: 'Interchange Plus', value: 'IC_PLUS' },
									{ label: 'Tiered', value: 'TIERED' },
									{ label: 'Flat Rate', value: 'FLAT_TIERED' },
								].map((config) => ({
									value: config.value || '',
									label: config.label,
									isDefault: config.value === 'IC_PLUS',
								}))}
								onChange={(value) => {
									const nextType = value as UiRateType;
									setSelectedRateTypes([nextType]);
									// Keep the picked file: every statement type accepts the same formats.
									setError('');
									setBusinessType('retail');
								}}
								placeholder="Select Statement Type"
								selectClassName="bg-white"
								containerClassName="w-2/3"
								fullWidth={false}
							/>
							<SelectInput
								label="Select Fee Structure Config"
								value={selectedConfigId}
								options={configs.map((config) => ({
									value: config.id || '',
									label: config.name,
									isDefault: config.is_default,
									isCustom: config.name.toLowerCase().includes('custom'),
								}))}
								onChange={setSelectedConfigId}
								onViewRates={() => setShowViewRatesModal(true)}
								viewRatesLabel="View Rates"
								disabled={uploading || isLoadingConfigs}
								placeholder={
									isLoadingConfigs
										? 'Loading configurations...'
										: 'Select a configuration'
								}
								selectClassName="bg-white"
								viewRatesButtonClassName="outline outline-1 outline-offset-[-1px] outline-neutral-200"
							/>
						</div>

						{/* Output Format Selection - Show for all statement types */}
						<div className="flex flex-row gap-2 align-center">
							<SelectInput
								label="Output Format"
								value={targetRateType || inputStatementType}
								options={outputFormatOptions}
								onChange={(value) => setTargetRateType(value as UiRateType)}
								placeholder="Select output format"
								selectClassName="bg-white"
								disabled={uploading || !hasMultipleOutputOptions}
								helperText={
									hasMultipleOutputOptions
										? 'Select the pricing format for the comparison results'
										: 'No conversion available for this statement type'
								}
								containerClassName="w-1/2"
								fullWidth={false}
							/>
						</div>

						{/* Risk Tier Selection for Revenue Calculation */}
						<div className="flex flex-col gap-2">
							<SelectInput
								label="Merchant Risk Tier (for ISO profit analysis)"
								value={selectedRiskTier}
								options={[
									{ value: 'LOW', label: 'Low Risk', isDefault: true },
									{ value: 'MEDIUM', label: 'Medium Risk', isDefault: false },
									{ value: 'HIGH', label: 'High Risk', isDefault: false },
								]}
								onChange={(value) => setSelectedRiskTier(value as RiskTier)}
								placeholder="Select Risk Tier"
								selectClassName="bg-white"
								disabled={uploading}
								helperText="Used to calculate agent profit margins based on Schedule A buy rates"
							/>
						</div>

						{/* Flat Fields — shown for both flat dropdown items */}
						{isAnyFlat && (
							<div className="flex flex-col gap-4 p-4 bg-blue-50 rounded-md border border-blue-200">
								<div className="text-sm font-medium text-blue-900">
									Flat-Rate Statement Details
								</div>
								<div className="flex flex-col gap-4">
									<SelectInput
										label="Business Type *"
										value={businessType}
										options={[
											{
												value: 'retail',
												label: 'Retail / Online / CNP',
												isDefault: true,
											},
											{
												value: 'restaurant',
												label: 'Restaurant',
												isDefault: false,
											},
										]}
										onChange={setBusinessType}
										placeholder="Select business type"
										selectClassName="bg-white"
										disabled={uploading}
										helperText="Select your primary business type for accurate interchange estimates"
									/>
								</div>
							</div>
						)}

						{/* File Upload Area */}
						<div className="flex-1">
							<FileUploadDropzone
								title="Drag and drop file here"
								description={`${STATEMENT_UPLOAD_HINT}${monthsAllowed ? ' · up to three months for one merchant' : ''}`}
								buttonText="Choose File"
								accept={STATEMENT_UPLOAD_ACCEPT}
								maxSize={10}
								onFileSelect={(f) => handleFiles([f])}
								onFilesSelect={handleFiles}
								selectedFiles={selectedFiles}
								selectedFilesCaption={
									monthsMode && selectedFiles.length > 1
										? `${selectedFiles.length} months for one merchant. Each file is checked on its own.`
										: undefined
								}
								onFileRemove={removeFile}
								selectedFile={selectedFile}
								dragActive={dragActive}
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}
								disabled={uploading}
								error={error}
								containerClassName="outline-zinc-400"
								iconClassName="text-[#ACB1B7]"
								titleClassName="text-black"
								descriptionClassName="text-zinc-500"
								buttonClassName="bg-gray-100 hover:bg-gray-200"
							/>
						</div>

						{monthsMode && (
							<div>
								<label
									htmlFor="upMerchant"
									className="mb-2 block text-sm font-medium text-gray-700"
								>
									Merchant
								</label>
								<input
									id="upMerchant"
									className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm leading-5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
									placeholder="Merchant name"
									value={merchantName}
									onChange={(e) => setMerchantName(e.target.value)}
									disabled={uploading}
									maxLength={255}
								/>
								<div className="mt-2 text-xs text-gray-500">
									The months become one merchant record. Enter the merchant&apos;s
									name.
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center gap-2.5 p-5">
					<Button
						variant="outline"
						onClick={handleCancel}
						disabled={uploading}
						className="flex-1 h-10 px-5 bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-200 text-zinc-800 text-sm font-medium"
					>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="primary"
						onClick={handleSubmit}
						disabled={uploading || !selectedFile}
						loading={uploading}
						className="flex-1 h-10 px-5 bg-primary rounded-md text-white text-sm font-medium"
					>
						{monthsMode && selectedFiles.length > 1
							? `Upload ${selectedFiles.length} Statements`
							: 'Upload Statement'}
					</Button>
				</div>
			</div>

			{/* ViewRates Modal */}
			{showViewRatesModal && selectedConfigId && (
				<ViewRatesModal
					configId={selectedConfigId}
					onClose={() => setShowViewRatesModal(false)}
					onCustomize={(fileId) => {
						setSelectedFileId(fileId || null);
						setShowCustomizeModal(true);
						setShowViewRatesModal(false);
					}}
				/>
			)}

			{/* Customize Fee Structure Modal */}
			{showCustomizeModal && selectedConfigId && (
				<CustomizeFeeStructureModal
					isOpen={showCustomizeModal}
					onClose={() => setShowCustomizeModal(false)}
					onConfirm={() => {
						setShowCustomizeModal(false);
						setShowViewRatesModal(false);
					}}
					configId={selectedConfigId}
					fileId={selectedFileId || ''}
					context="upload"
				/>
			)}
		</div>
	);
};

export default StatementUpload;
